import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { map } from 'rxjs/operators';
import { StorageService } from '@src/app/services/storage/storage.service.interface';
import { StorageSelectors } from '@src/app/services/storage/storage.selectors';
import _ from 'lodash';
import { RetailerScope } from '@src/app/model/retailer-scope';

/**
 * Service that maintains a current scope. Also provides a stream that gives all retailers that
 * can be used as current scope.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class ScopeService implements OnDestroy {
  private _serviceDestroyed$: Subject<void> = new Subject();

  /**
   * Stream of ALL possible scopes. A scope corresponds with a retailer.
   */
  public readonly scopes$: Observable<RetailerScope[]>;

  /**
   * The current scope
   */
  public readonly currentScope$: BehaviorSubject<RetailerScope> = new BehaviorSubject<RetailerScope>(null);

  constructor(
    private _apolloService: ApolloService,
    private _storageService: StorageService
  ) {
    const storedScope = this._storageService.readFromStorage<RetailerScope>(StorageSelectors.retailerScope);
    if (!_.isEmpty(storedScope)) {
      this.setScope(storedScope);
    }

    this.scopes$ = this._apolloService.getAllPublicRetailers$().pipe(
      takeUntil(this._serviceDestroyed$),
      map(x => x.retailers),
      map(x => x.map(r => new RetailerScope(r.id, r.name))),
      withLatestFrom(this.currentScope$),
      map(([x, y]) => {
        if (!y) {
          // no known scope? set to first available
          this.setScope(x[0]);
        }
        // only return the scope array
        return x;
      })
    );
  }

  public setScope(scope: RetailerScope) {
    this._storageService.setInStorage<RetailerScope>(StorageSelectors.retailerScope, scope);
    this.currentScope$.next(scope);
  }

  public resetScope() {
    this._storageService.removeFromStorage(StorageSelectors.retailerScope);
    this.currentScope$.next(null);
  }

  /**
   * On destroy close subscriptions
   */
  public ngOnDestroy(): void {
    this._serviceDestroyed$.next();
    this._serviceDestroyed$.complete();
  }
}
